import { FC, useState, useRef, useCallback, ChangeEvent, useEffect } from 'react';
import { BigCirclePlus, CameraIcon, DialogWithActions, PhotoStepPopup, ImageCropDialog } from 'Components';
import { useLocalization } from 'Hooks';
import { Box, Typography, Button } from '@material-ui/core';
import { colors, fontSizes, gutters } from 'Theme/Variables';
import { checkCameraPermission, getBase64ImageType, getImageFromDevice } from 'Utils';
import checkPhotoValidity from 'Services/Queries/CheckPhotoValidity';
import { localStorageKeys } from 'Config';
import { UsingPhotoDesktop, UsingPhotoMobile } from 'Assets/Images/usingPhoto';

import Webcam from 'react-webcam';

import './AddPhotoStep.scss';
import { ApplicationProcessState, applicationStore, incStep, setApplicationData } from 'Stores/CardApplication';
import { useDispatch, useSelector } from 'react-redux';
import CameraContainer from 'Assets/Images/cameraContainer.png';
import { UserState, userStore } from 'Stores/User';
import { show505ErrorDialog } from 'Stores/Dialog';

type AddPhotoStepProps = {};

const AddPhotoStep: FC<AddPhotoStepProps> = () => {
  const dispatch = useDispatch();
  const userData: UserState = useSelector(userStore);

  const webcamRef: any = useRef(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const store: ApplicationProcessState = useSelector(applicationStore);
  const { activeApplicationData } = store;

  const [isPermissionGranted, setIsPermissionGrandted] = useState<boolean>(false);
  const [isOpenCamera, setIsOpenCamera] = useState<boolean>(false);
  const [isOpenPhotoDialog, setIsOpenPhotoDialog] = useState<boolean>(false);
  const [isOpenRuleDialog, setIsOpenRuleDialog] = useState<boolean>(false);
  const [isOpenIntroDialog, setIsOpenIntroDialog] = useState<boolean>(false);
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState<boolean>(false);
  const [isOpenCropDialog, setIsOpenCropDialog] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isCheckingPhoto, setIsCheckingPhoto] = useState<boolean>(false);

  const [photoSrc, setPhotoSrc] = useState<string | undefined>(activeApplicationData?.DocumentData);
  const [tempPhotoSrc, setTempPhotoSrc] = useState<string | undefined>(undefined);
  const [imageType, setImageType] = useState<string | undefined>(activeApplicationData?.MimeType);
  const [selectedFileName, setSelectedFileName] = useState<string>(activeApplicationData?.FileName || '');

  // Store'dan gelen filename değeri değiştiğinde local state'i güncelle
  useEffect(() => {
    if (activeApplicationData?.FileName) {
      setSelectedFileName(activeApplicationData.FileName);
    }
  }, [activeApplicationData?.FileName]);

  // Component mount olduğunda fotoğraf popup resimlerini önceden yükle
  useEffect(() => {
    const images = [
      ...Object.values(UsingPhotoDesktop),
      ...Object.values(UsingPhotoMobile),
    ];
    images.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Component unmount olduğunda localStorage'ı temizle
  useEffect(() => {
    return () => {
      localStorage.removeItem(localStorageKeys.addPhotoStepIntroShown);
    };
  }, []);

  const localization = useLocalization() as any;

  const {
    web_card_application_add_photo,
    web_card_application_click_to_add,
    web_photo_rules_link_btn,
    web_btn_next,
    web_photo_adding_method_pc,
    web_photo_adding_method_title,
    web_photo_adding_method_webcam,
    web_take_photo_btn,
    web_btn_vazgec,
    web_photo_click_to_change,
    web_photo_rules_popup_btn,
    web_btn_okay,
    web_istanbulcard_apply_page_popup_title,
    web_istanbulcard_apply_page_photo_popup_btn_add_photo,
    web_istanbulcard_apply_page_photo_popup_text,
  } = localization || {};

  const infoTitle = localization?.web_pc_istanbulcard_apply_page_photo_info_title || 'Bilgilendirme';
  const infoDesc =
    localization?.web_pc_istanbulcard_apply_page_photo_info_desc ||
    'Fotoğrafınızı yüklerken, yüzünüz ve kimliğinizin aynı karede net bir şekilde görünecek şekilde hizalanmış ve uygun boyutlandırılmış olmasına dikkat edin.';

  const capture = useCallback(() => {
    // Yüksek kalitede screenshot al (quality: 1.0 = maksimum kalite)
    const imageSrc: string = webcamRef?.current?.getScreenshot({
      width: 1920,
      height: 1920,
    });
    setImageType(getBase64ImageType(imageSrc));
    setIsOpenCamera(false);
    setTempPhotoSrc(imageSrc);
    setSelectedFileName('webcam');
    setIsOpenCropDialog(true);
  }, [webcamRef]);

  const getCameraPermission = async () => {
    setIsOpenCamera(true);
    await checkCameraPermission(setIsPermissionGrandted);
  };

  const checkPhoto = async () => {
    if (isCheckingPhoto) return;

    setIsCheckingPhoto(true);
    try {
      const response = await checkPhotoValidity({
        ProductCode: store.applicationCard?.ProductCode || '',
        MimeType: imageType,
        DocumentData: photoSrc?.split(',')[1],
        CustomerNumber: userData.customerNumber,
        SessionToken: userData.sessionToken,
      });
      
      const responseCode = response?.data?.ResponseCode;
      
      if (responseCode === '0') {
        dispatch(
          setApplicationData({
            FileName: selectedFileName,
            DocumentData: photoSrc,
            MimeType: imageType,
          })
        );
        dispatch(incStep());
      } else if (responseCode === '505' || responseCode === '506' || responseCode === '507') {
        // Oturum kodu hatalı - popup aç
        dispatch(show505ErrorDialog());
      } else {
        setErrorMessage(web_istanbulcard_apply_page_photo_popup_text || response?.data?.ResponseDescription);
        setIsOpenErrorDialog(true);
      }
    } catch (error) {
      // Hata durumunda da loading state'i temizle
    } finally {
      setIsCheckingPhoto(false);
    }
  };

  const choosedFromPc = async (event: ChangeEvent<HTMLInputElement>) => {
    const { type, src } = await getImageFromDevice(event);
    setImageType(type);
    if (src !== '') {
      setTempPhotoSrc(src);
      setIsOpenCropDialog(true);
    }
    // Dosya ismini al
    const file = event.target.files?.[0];
    if (file) {
      // Dosya uzantısını kaldır ve sadece ismi al
      const fileNameWithoutExtension = file.name.replace(/\.[^/.]+$/, '');
      setSelectedFileName(fileNameWithoutExtension || '');
    }
    setIsOpenPhotoDialog(false);
    // Input'u resetle - aynı dosya tekrar seçilebilsin
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleCropSave = (croppedImage: string, mimeType: string) => {
    setPhotoSrc(croppedImage);
    // MimeType'ı 'image/jpeg' -> 'jpeg' formatına çevir
    const imageFormat = mimeType.replace('image/', '');
    setImageType(imageFormat);
    setIsOpenCropDialog(false);
  };

  return (
    <div id="addPhotoStep">
      <Typography variant="h3" component="h2" align="center">
        <Box mt={gutters.bigger} color={colors.secondary} fontWeight={600}>
          {web_card_application_add_photo}
        </Box>
      </Typography>
      <input onChange={choosedFromPc} ref={inputRef} hidden accept="image/png, image/jpg, image/jpeg" type="file" />
      <div className="addPhotoStep">
        <div

          onClick={() => {
            if (!isOpenCamera) {
              if (photoSrc) {
                setIsOpenPhotoDialog(true);
              } else {
                // localStorage'dan kontrol et, daha önce gösterildi mi?
                const hasSeenIntro = localStorage.getItem(localStorageKeys.addPhotoStepIntroShown);
                if (!hasSeenIntro) {
                  setIsOpenIntroDialog(true);
                } else {
                  setIsOpenPhotoDialog(true);
                }
              }
            }
          }}
          tabIndex={0}
          onKeyDown={(e)=>{
            if(e.key==='Enter' || e.key===' '){
              e.preventDefault();
              if (!isOpenCamera) {
              if (photoSrc) {
                setIsOpenPhotoDialog(true);
              } else {
                // localStorage'dan kontrol et, daha önce gösterildi mi?
                const hasSeenIntro = localStorage.getItem(localStorageKeys.addPhotoStepIntroShown);
                if (!hasSeenIntro) {
                  setIsOpenIntroDialog(true);
                } else {
                  setIsOpenPhotoDialog(true);
                }
              }
            }
            }
          }}
          role="button"
          aria-label="Fotoğraf eklemek için tıkla"
          className="addPhotoStep__area"
          itemID="openCameraDialogBtnTest"
        >
          {photoSrc ? (
            <img src={photoSrc} className="addPhotoStep__img" alt="Yüklenen fotoğraf önizlemesi"/>
          ) : !isOpenCamera || !isPermissionGranted ? (
            <>
              <BigCirclePlus />
              <Typography variant="h3" component="p" align="center">
                <Box color={colors.darkGray} fontSize={fontSizes.smaller}>
                  {web_card_application_click_to_add}
                </Box>
              </Typography>
            </>
          ) : (
            <div className="cameraContainer">
              <Webcam
                ref={webcamRef}
                videoConstraints={{
                  width: { ideal: 1920 },
                  height: { ideal: 1920 },
                  facingMode: 'user',
                  aspectRatio: 1,
                }}
                screenshotFormat="image/jpeg"
                screenshotQuality={1.0}
                width={200}
                height={200}
                className="webcam"
                audio={false}
              />
              <img src={CameraContainer} alt="" aria-hidden="true" />
            </div>
          )}
        </div>

        {!!photoSrc && (
          <>
            <Typography variant="h3" component="span" align="center">
              <Box
                onClick={() => setIsOpenPhotoDialog(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e)=>{
                  if(e.key==='Enter' ||e.key===' '){
                    e.preventDefault();
                    setIsOpenPhotoDialog(true);
                  }
                }}
                className="addPhotoStep__rules"
                mt={gutters.regular}
                mb={gutters.small}
                color={colors.secondary}
                fontSize={fontSizes.tiny}
                itemID="changePhotoBtnTest"
              >
                {web_photo_click_to_change}
              </Box>
            </Typography>
            <Typography variant="h3" component="p" align="center">
              <Box
                onClick={() => {
                  setIsOpenRuleDialog(true);
                  // Rule dialog açıldığında da localStorage'dan sil ki tekrar gösterilsin
                  localStorage.removeItem(localStorageKeys.addPhotoStepIntroShown);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e)=>{
                  if(e.key==='Enter' ||e.key===' '){
                    e.preventDefault();
                    setIsOpenRuleDialog(true);
                    localStorage.removeItem(localStorageKeys.addPhotoStepIntroShown);
                  }
                }}
                className="addPhotoStep__rules"
                mb={gutters.regular}
                color={colors.primary}
                fontSize={fontSizes.smaller}
                itemID="openPhotoRuleBtnTest"
              >
                {web_photo_rules_link_btn}
              </Box>
            </Typography>
          </>
        )}

        <Box mt={!!photoSrc ? gutters.regular : gutters.big} mb={gutters.regular} className="addPhotoStep__info">
          <Typography variant="body2" component="h3">
            <Box fontWeight={600} fontSize={fontSizes.small} mb={0.5} color={colors.secondary}>
              {infoTitle}
            </Box>
          </Typography>
          <Typography variant="body2" component="h3">
            <Box fontWeight={300} color={colors.checkboxLabel} fontSize={fontSizes.smaller}>
              {infoDesc}
            </Box>
          </Typography>
        </Box>
        {!isOpenCamera || !isPermissionGranted ? (
          <Button
            disabled={!!!photoSrc || isCheckingPhoto}
            onClick={() => photoSrc && checkPhoto()}
            variant="contained"
            color="secondary"
            fullWidth
            itemID="loadImageBtnTest"
          >
            {web_btn_next}
          </Button>
        ) : (
          <>
            <Button
              startIcon={<CameraIcon />}
              onClick={capture}
              variant="outlined"
              color="secondary"
              fullWidth
              itemID="takePhotoBtnTest"
            >
              {web_take_photo_btn}
            </Button>
            <Box mt={gutters.small} />
            <Button
              style={{ color: 'red' }}
              onClick={() => setIsOpenCamera(false)}
              variant="text"
              color="default"
              fullWidth
              itemID="closeCameraBtnTest"
            >
              {web_btn_vazgec}
            </Button>
          </>
        )}
      </div>
      <DialogWithActions
        alignTitle="left"
        closeIcon
        primaryButton={{
          handleClick: () => {
            inputRef.current?.click();
          },
          label: web_photo_adding_method_pc,
        }}
        secondaryButton={{
          handleClick: () => {
            setPhotoSrc(undefined);
            getCameraPermission();
            setIsOpenPhotoDialog(false);
          },
          label: web_photo_adding_method_webcam,
          type: 'contained',
        }}
        open={isOpenPhotoDialog}
        hide={() => setIsOpenPhotoDialog(false)}
        title={web_photo_adding_method_title}
      />
      <PhotoStepPopup
        isOpen={isOpenIntroDialog || isOpenRuleDialog}
        buttonText={isOpenRuleDialog ? web_btn_okay : web_photo_rules_popup_btn}
        onClick={() => {
          if (isOpenIntroDialog) {
            // Intro dialog kapatılırken localStorage'a kaydet
            localStorage.setItem(localStorageKeys.addPhotoStepIntroShown, 'true');
          }
          setIsOpenIntroDialog(false);
          if (!isOpenRuleDialog) {
            setIsOpenPhotoDialog(true);
          }
          setIsOpenRuleDialog(false);
        }}
      />
      <DialogWithActions
        open={isOpenErrorDialog}
        hide={() => setIsOpenErrorDialog(false)}
        title={web_istanbulcard_apply_page_popup_title}
        text={errorMessage}
        primaryButton={{
          label: web_istanbulcard_apply_page_photo_popup_btn_add_photo,
          handleClick: () => {
            setIsOpenErrorDialog(false);
            setIsOpenPhotoDialog(true);
          },
        }}
      />
      {tempPhotoSrc && (
        <ImageCropDialog
          open={isOpenCropDialog}
          hide={() => setIsOpenCropDialog(false)}
          imageSrc={tempPhotoSrc}
          onSave={handleCropSave}
        />
      )}
    </div>
  );
};
export default AddPhotoStep;
